import { useEffect, useRef, useState } from "react";
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";
import { issueHostStreamTicket, dockerWsUrl } from "@/api/endpoints/docker";

interface Props {
  hostId: number;
  cid: string;
}

/**
 * Interactive shell into a live container (Docker-Desktop "Exec" tab), backed by
 * the backend's bidirectional WebSocket proxy. Binary frames carry stdin/stdout;
 * text frames carry control JSON (terminal resize).
 */
export function ExecTerminal({ hostId, cid }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState("connexion…");

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const term = new Terminal({
      cursorBlink: true,
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      fontSize: 13,
      theme: { background: "#0a0a0a", foreground: "#e5e7eb" },
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(el);
    fit.fit();

    let ws: WebSocket | null = null;
    let cancelled = false;
    const enc = new TextEncoder();

    const sendResize = () => {
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(
          JSON.stringify({ resize: { cols: term.cols, rows: term.rows } }),
        );
      }
    };

    (async () => {
      try {
        const ticket = await issueHostStreamTicket(hostId, cid, "exec");
        if (cancelled) return;
        ws = new WebSocket(
          dockerWsUrl(
            `/docker/hosts/${hostId}/live/containers/${cid}/exec`,
            ticket,
          ),
        );
        ws.binaryType = "arraybuffer";
        ws.onopen = () => {
          setStatus("connecté");
          sendResize();
          term.focus();
        };
        ws.onmessage = (e) => {
          if (e.data instanceof ArrayBuffer) {
            term.write(new Uint8Array(e.data));
          } else {
            term.write(e.data as string);
          }
        };
        ws.onclose = () => {
          setStatus("session fermée");
          term.write("\r\n\x1b[33m[session terminée]\x1b[0m\r\n");
        };
        ws.onerror = () => setStatus("erreur");
      } catch {
        setStatus("erreur ticket");
      }
    })();

    // Keystrokes -> stdin (binary frames).
    const onData = term.onData((d) => {
      if (ws?.readyState === WebSocket.OPEN) ws.send(enc.encode(d));
    });
    const onResize = term.onResize(sendResize);

    const ro = new ResizeObserver(() => {
      try {
        fit.fit();
      } catch {
        /* element detached */
      }
    });
    ro.observe(el);

    return () => {
      cancelled = true;
      ro.disconnect();
      onData.dispose();
      onResize.dispose();
      ws?.close();
      term.dispose();
    };
  }, [hostId, cid]);

  return (
    <div className="flex h-full flex-col">
      <div className="px-1 pb-1 text-xs text-gray-500">
        Terminal · <span className="text-gray-400">{status}</span>
      </div>
      <div
        ref={containerRef}
        className="min-h-0 flex-1 overflow-hidden rounded bg-[#0a0a0a] p-2"
      />
    </div>
  );
}
